import React, { useEffect, useState } from 'react';
import { db } from '../services/firebaseConfig';
import { collection, query, orderBy, getDocs } from "firebase/firestore";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faChartLine,
  faMoneyBillWave,
  faShieldAlt,
  // faChartPie,
  // faCalendarAlt,
  faExpand,
  faStar,
  faUserFriends,
  faTimes,
  faCompress,
  faDownload,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
  Line
} from 'recharts';
import './AdminDashboard.css';
// import Header from './Header';
import Footer from './Footer';
import { Helmet } from "react-helmet";


<Helmet>
  <title>Company Dashboard | Internal Access | Kora Insurance </title>
  <meta name="description" content="Protect your family with Kora's flexible and affordable insurance plans. Sign up in minutes and stay covered." />
  <meta name="keywords" content="Insurance Ghana, Family Insurance, Affordable Cover, Kora Insurance" />
  <meta name="robots" content="noindex, nofollow" />
  <link rel="canonical" href="https://korame.com.gh"/>
  {/* Open Graph tags */}
  <meta property="og:title" content="Best Online Insurance Platform" />
  <meta property="og:description" content="Compare and buy insurance plans with ease." />
  <meta property="og:url" content="https://korame.com.gh/" />
  <meta property="og:type" content="website" />
</Helmet>

const AdminDashboard = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalPremium: 0,
    averagePremium: 0,
    coverageDistribution: {},
    monthlyTrends: [],
    ageGroupDistribution: {},
    mostPopularPlan: 'N/A',
    avgBeneficiaries: '0'
  });
  const [showAllSubmissions, setShowAllSubmissions] = useState(false);
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [showPremiumBreakdown, setShowPremiumBreakdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(15);
  const [sortField, setSortField] = useState('submittedAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showGraphModal, setShowGraphModal] = useState(false);
  const [timeRange, setTimeRange] = useState('1Y');
  const [filteredUsers, setFilteredUsers] = useState(submissions);
  const [dashboardSearchTerm, setDashboardSearchTerm] = useState('');
  const [filteredDashboardUsers, setFilteredDashboardUsers] = useState(submissions);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  const RECENT_SUBMISSIONS_COUNT = 7;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const q = query(collection(db, 'users_new'), orderBy('submittedAt', 'desc'));
        const querySnapshot = await getDocs(q);
        const data = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          submittedAt: doc.data().submittedAt?.toDate()
        }));
        setSubmissions(data);

        // Calculate dashboard statistics
        const stats = calculateStats(data);
        setStats(stats);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const results = submissions.filter(user =>
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phoneNumber?.includes(searchTerm) ||
      user.coverOption?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(results);
  }, [searchTerm, submissions]);

  useEffect(() => {
    const results = submissions.filter(user =>
      user.firstName?.toLowerCase().includes(dashboardSearchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(dashboardSearchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(dashboardSearchTerm.toLowerCase()) ||
      user.phoneNumber?.includes(dashboardSearchTerm) ||
      user.coverOption?.toLowerCase().includes(dashboardSearchTerm.toLowerCase())
    );
    setFilteredDashboardUsers(results);
  }, [dashboardSearchTerm, submissions]);

  const calculateStats = (data) => {
    const totalUsers = data.length;
    const totalPremium = data.reduce((sum, item) => sum + (Number(item.annualPremium) || 0), 0);
    const averagePremium = totalUsers > 0 ? totalPremium / totalUsers : 0;

    // Calculate coverage distribution
    const coverageDistribution = data.reduce((acc, item) => {
      acc[item.coverOption] = (acc[item.coverOption] || 0) + 1;
      return acc;
    }, {});

    // Calculate age group distribution
    const ageGroupDistribution = data.reduce((acc, item) => {
      acc[item.ageGroup] = (acc[item.ageGroup] || 0) + 1;
      return acc;
    }, {});

    // Calculate most popular cover option
    const mostPopularPlan = Object.entries(coverageDistribution)
      .sort(([,a], [,b]) => b - a)[0]?.[0] || 'N/A';

    // Calculate average beneficiaries
    const totalBeneficiaries = data.reduce((sum, item) => {
      return sum + (item.beneficiaries?.length || 0);
    }, 0);
    const avgBeneficiaries = totalUsers > 0 ? (totalBeneficiaries / totalUsers).toFixed(1) : '0';

    // Calculate monthly trends
    const monthlyTrends = calculateMonthlyTrends(data);

    return {
      totalUsers,
      totalPremium,
      averagePremium,
      coverageDistribution,
      monthlyTrends,
      ageGroupDistribution,
      mostPopularPlan,
      avgBeneficiaries
    };
  };

  const calculateMonthlyTrends = (data) => {
    const monthlyData = data.reduce((acc, item) => {
      const date = new Date(item.submittedAt);
      const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`;

      if (!acc[monthYear]) {
        acc[monthYear] = {
          count: 0,
          premium: 0
        };
      }

      acc[monthYear].count += 1;
      acc[monthYear].premium += Number(item.annualPremium) || 0;

      return acc;
    }, {});

    return Object.entries(monthlyData)
      .map(([month, data]) => ({
        month,
        submissions: data.count,
        premium: data.premium
      }))
      .slice(-6); // Last 6 months
  };

  const filteredSubmissions = submissions.filter(user =>
    user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phoneNumber?.includes(searchTerm) ||
    user.payeeGhanaCardNumber?.includes(searchTerm) ||
    user.paystackRef?.includes(searchTerm) ||
    // Search in beneficiaries
    user.beneficiaries?.some(ben =>
      ben.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ben.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ben.phoneNumber?.includes(searchTerm) ||
      ben.ghanaCardNumber?.includes(searchTerm)
    )
  );

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedUsers(filteredSubmissions.map(user => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (id) => {
    if (selectedUsers.includes(id)) {
      setSelectedUsers(selectedUsers.filter(i => i !== id));
    } else {
      setSelectedUsers([...selectedUsers, id]);
    }
  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;

  const sortData = (data) => {
    if (!data) return [];
    
    return [...data].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
  
      // Handle date sorting
      if (sortField === 'submittedAt') {
        aValue = aValue instanceof Date ? aValue : new Date(aValue);
        bValue = bValue instanceof Date ? bValue : new Date(bValue);
      }
  
      // Handle string sorting
      if (['firstName', 'lastName', 'coverOption'].includes(sortField)) {
        aValue = (aValue || '').toLowerCase();
        bValue = (bValue || '').toLowerCase();
      }
  
      // Handle number sorting
      if (sortField === 'annualPremium') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }
  
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedData = sortData(filteredSubmissions);
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredSubmissions.length / rowsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSortChange = (event) => {
    const [field, direction] = event.target.value.split('-');
    setSortField(field);
    setSortDirection(direction);
  };

  // Modal components
  const SubmissionsModal = () => (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>All Submissions</h2>
          <FontAwesomeIcon
            icon={faTimes}
            className="close-icon"
            onClick={() => setShowAllSubmissions(false)}
          />
        </div>
        <div className="modal-body">
          <div className="table-controls">
            <div className="sort-controls">
              <label htmlFor="sortSelect">Sort by: </label>
              <select
                id="sortSelect"
                value={`${sortField}-${sortDirection}`}
                onChange={handleSortChange}
                className="sort-select"
              >
                <option value="submittedAt-desc">Date (Newest First)</option>
                <option value="submittedAt-asc">Date (Oldest First)</option>
                <option value="firstName-asc">First Name (A-Z)</option>
                <option value="firstName-desc">First Name (Z-A)</option>
                <option value="lastName-asc">Last Name (A-Z)</option>
                <option value="lastName-desc">Last Name (Z-A)</option>
                <option value="coverOption-asc">Cover Option (A-Z)</option>
                <option value="coverOption-desc">Cover Option (Z-A)</option>
              </select>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Name</th>
                <th>Email</th>
                <th>Cover Option</th>
                <th>Premium</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {currentRows.map((submission) => (
                <tr key={submission.id}>
                  <td>{new Date(submission.submittedAt).toLocaleDateString()}</td>
                  <td>{`${submission.firstName} ${submission.lastName}`}</td>
                  <td>{submission.email}</td>
                  <td>{submission.coverOption}</td>
                  <td>₵{Number(submission.annualPremium).toLocaleString()}</td>
                  <td>
                    <span className={`status ${submission.status || 'pending'}`}>
                      {submission.status || 'Pending'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pagination">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              Previous
            </button>

            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index + 1}
                onClick={() => handlePageChange(index + 1)}
                className={`pagination-button ${currentPage === index + 1 ? 'active' : ''}`}
              >
                {index + 1}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-button"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const UsersModal = ({ sortField, sortDirection , handleSortChange}) => {
    // Separate state for modal search
    const [modalSearchTerm, setModalSearchTerm] = useState('');
    const [filteredUsers, setFilteredUsers] = useState(submissions);

    // Separate search effect for modal
    useEffect(() => {
      const results = submissions.filter(user =>
        user.firstName?.toLowerCase().includes(modalSearchTerm.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(modalSearchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(modalSearchTerm.toLowerCase()) ||
        user.phoneNumber?.includes(modalSearchTerm) ||
        user.coverOption?.toLowerCase().includes(modalSearchTerm.toLowerCase())
      );
      setFilteredUsers(sortData(results));
    }, [modalSearchTerm, submissions, sortField, sortDirection]);

    const downloadCSV = () => {
      // Define the headers
      const headers = [
        'Date',
        'First Name',
        'Last Name',
        'Email',
        'Phone',
        'Cover Option',
        'Annual Premium',
        'Payment Status',
        'Number of added lives'
      ];

      // Format the data
      const csvData = submissions.map(user => [
        new Date(user.submittedAt).toLocaleDateString(),
        user.firstName,
        user.lastName,
        user.email,
        `${user.countryCode} ${user.phoneNumber}`,
        user.coverOption,
        `₵${Number(user.annualPremium).toLocaleString()}`,
        user.status || 'Pending',
        user.beneficiaries?.length || 0
      ]);

      // Combine headers and data
      const csvContent = [
        headers,
        ...csvData
      ].map(row => row.join(',')).join('\n');

      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `insurance_submissions_${new Date().toLocaleDateString()}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    return (
      <div className={`modal-overlay ${isFullscreen ? 'fullscreen' : ''}`}>
        <div className={`modal-content large ${isFullscreen ? 'fullscreen' : ''}`}>
          <div className="modal-header">
            <h2>All Users</h2>
            <div className="modal-controls">
              <button 
                onClick={downloadCSV}
                className="download-btn"
              >
                <FontAwesomeIcon icon={faDownload} className="download-icon" />
                Download CSV
              </button>
              <FontAwesomeIcon 
                icon={isFullscreen ? faCompress : faExpand} 
                className="control-icon"
                data-icon="expand"
                onClick={() => setIsFullscreen(!isFullscreen)}
              />
              <FontAwesomeIcon 
                icon={faTimes} 
                className="close-icon"
                onClick={() => {
                  setShowAllUsers(false);
                  setIsFullscreen(false);
                }}
              />
            </div>
          </div>
          <div className="modal-search-container">
            <div className="modal-search-bar">
              <FontAwesomeIcon icon={faSearch} className="modal-search-icon" />
              <input
                type="text"
                placeholder="Search by name, email, phone, or cover option..."
                value={modalSearchTerm}
                onChange={(e) => setModalSearchTerm(e.target.value)}
                className="modal-search-input"
              />
              {modalSearchTerm && (
                <FontAwesomeIcon 
                  icon={faTimes} 
                  className="modal-clear-search" 
                  onClick={() => setModalSearchTerm('')}
                />
              )}
            </div>
            <p className="modal-results-count">
              Showing {filteredUsers.length} of {submissions.length} users
            </p>
          </div>
          <div className="modal-body">
            <div className="table-responsive">
              <div className="table-controls">
                <div className="sort-controls">
                  <label htmlFor="sortSelect">Sort by: </label>
                  <select
                    id="sortSelect"
                    value={`${sortField}-${sortDirection}`}
                    onChange={handleSortChange}
                    className="sort-select"
                  >
                    <option value="submittedAt-desc">Date (Newest First)</option>
                    <option value="submittedAt-asc">Date (Oldest First)</option>
                    <option value="firstName-asc">First Name (A-Z)</option>
                    <option value="firstName-desc">First Name (Z-A)</option>
                    <option value="lastName-asc">Last Name (A-Z)</option>
                    <option value="lastName-desc">Last Name (Z-A)</option>
                    <option value="coverOption-asc">Cover Option (A-Z)</option>
                    <option value="coverOption-desc">Cover Option (Z-A)</option>
                  </select>
                </div>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={selectedUsers.length === currentRows.length}
                      />
                    </th>
                    <th>Date</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Cover Option</th>
                    <th>Premium</th>
                    <th>Beneficiary</th>

                    {/* <th>Payment Status</th> */}
                    <th>Added Lives</th>

                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr
                      key={user.id}
                      className={selectedUsers.includes(user.id) ? 'selected-row' : ''}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => handleSelectUser(user.id)}
                        />
                      </td>
                      <td>{user.submittedAt instanceof Date
                        ? user.submittedAt.toLocaleDateString()
                        : new Date(user.submittedAt).toLocaleDateString()}</td>
                      <td>{user.firstName}</td>
                      <td>{user.lastName}</td>
                      <td>{user.email}</td>
                      <td>{`${user.countryCode} ${user.phoneNumber}`}</td>
                      <td>{user.coverOption}</td>
                      <td>₵{Number(user.annualPremium).toLocaleString()}</td>
                      <td>{user.nextofkin || "N/A"}</td>

                      {/* <td>
                        <span className={`status ${user.status || 'pending'}`}>
                          {user.status || 'Pending'}
                        </span>
                      </td> */}
                      <td className="beneficiaries-cell">
                        {user.beneficiaries?.length || 0}
                        <div className="beneficiary-tooltip">
                          {user.beneficiaries?.map((ben, index) => (
                            <div key={index} className="beneficiary-info">
                              <strong>{index + 1}:</strong><br />
                              Name: {`${ben.firstName} ${ben.lastName}`}<br />
                              Relationship: {ben.relationship}
                              {index < user.beneficiaries.length - 1 && <hr />}
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-button"
                >
                  Previous
                </button>

                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => handlePageChange(index + 1)}
                    className={`pagination-button ${currentPage === index + 1 ? 'active' : ''}`}
                  >
                    {index + 1}
                  </button>
                ))}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-button"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const PremiumBreakdownModal = () => (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Premium Breakdown</h2>
          <FontAwesomeIcon
            icon={faTimes}
            className="close-icon"
            onClick={() => setShowPremiumBreakdown(false)}
          />
        </div>
        <div className="modal-body">
          <table>
            <thead>
              <tr>
                <th>Cover Option</th>
                <th>Number of Users</th>
                <th>Total Premium</th>
                <th>Percentage</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(stats.coverageDistribution).map(([cover, count]) => {
                const totalForCover = filteredSubmissions
                  .filter(s => s.coverOption === cover)
                  .reduce((sum, s) => sum + Number(s.annualPremium), 0);
                const percentage = ((totalForCover / stats.totalPremium) * 100).toFixed(1);

                return (
                  <tr key={cover}>
                    <td>{cover}</td>
                    <td>{count}</td>
                    <td>₵{totalForCover.toLocaleString()}</td>
                    <td>{percentage}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const GraphModal = () => (
    <div className={`modal-overlay ${isFullscreen ? 'fullscreen' : ''}`}>
      <div className={`modal-content large ${isFullscreen ? 'fullscreen' : ''}`}>
        <div className="modal-header">
          <div className="modal-title-section">
            <h2>Monthly Trends</h2>
            <div className="time-filter-group">
              <button
                className={`time-filter-btn ${timeRange === 'M' ? 'active' : ''}`}
                onClick={() => setTimeRange('M')}
              >
                1M
              </button>
              <button
                className={`time-filter-btn ${timeRange === '1Y' ? 'active' : ''}`}
                onClick={() => setTimeRange('1Y')}
              >
                1Y
              </button>
              <button
                className={`time-filter-btn ${timeRange === '5Y' ? 'active' : ''}`}
                onClick={() => setTimeRange('5Y')}
              >
                5Y
              </button>
            </div>
          </div>
          <div className="modal-controls">
            <FontAwesomeIcon
              icon={isFullscreen ? faCompress : faExpand}
              className="control-icon"
              onClick={() => setIsFullscreen(!isFullscreen)}
            />
            <FontAwesomeIcon
              icon={faTimes}
              className="close-icon"
              onClick={() => {
                setShowGraphModal(false);
                setIsFullscreen(false);
              }}
            />
          </div>
        </div>
        <div className="graph-container">
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={stats.monthlyTrends} margin={{ top: 20, right: 30, left: 40, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
              <XAxis
                dataKey="month"
                label={{
                  value: 'Time Period',
                  position: 'bottom',
                  offset: 0,
                  style: { textAnchor: 'middle', fill: '#64748b', fontSize: 14 }
                }}
              />
              <YAxis
                label={{
                  value: 'Number of Submissions',
                  angle: -90,
                  position: 'left',
                  offset: 0,
                  style: { textAnchor: 'middle', fill: '#64748b', fontSize: 14 }
                }}
              />
              <Tooltip
                contentStyle={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  border: 'none',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Bar dataKey="submissions" fill="#6366f1" radius={[4, 4, 0, 0]} />
              <Line
                type="monotone"
                dataKey="submissions"
                stroke="#2563eb"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6, fill: "#2563eb", stroke: "white", strokeWidth: 2 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  return (
    <>
      <div className="dashboard-container">
        <h1 className="dashboard-title">Insurance Admin Dashboard</h1>

        {/* Stats Grid */}
        <div className="stats-grid">
          <div className="stat-card clickable" onClick={() => setShowAllUsers(true)}>
            <FontAwesomeIcon icon={faUsers} className="stat-icon" />
            <div className="stat-content">
              <h3>Total Users</h3>
              <p>{stats.totalUsers}</p>
            </div>
          </div>

          <div className="stat-card clickable" onClick={() => setShowPremiumBreakdown(true)}>
            <FontAwesomeIcon icon={faMoneyBillWave} className="stat-icon" />
            <div className="stat-content">
              <h3>Total Premium</h3>
              <p>₵{stats.totalPremium.toLocaleString()}</p>
            </div>
          </div>

          <div className="stat-card">
            <FontAwesomeIcon icon={faChartLine} className="stat-icon" />
            <div className="stat-content">
              <h3>Average Premium</h3>
              <p>₵{stats.averagePremium.toLocaleString()}</p>
            </div>
          </div>

          <div className="stat-card">
            <FontAwesomeIcon icon={faShieldAlt} className="stat-icon" />
            <div className="stat-content">
              <h3>Active Policies</h3>
              <p>{stats.totalUsers}</p>
            </div>
          </div>

          <div className="stat-card">
            <FontAwesomeIcon icon={faStar} className="stat-icon" />
            <div className="stat-content">
              <h3>Most Popular Plan</h3>
              <p>{stats.mostPopularPlan}</p>
            </div>
          </div>

          <div className="stat-card">
            <FontAwesomeIcon icon={faUserFriends} className="stat-icon" />
            <div className="stat-content">
              <h3>Avg. Lives Added</h3>
              <p>{stats.avgBeneficiaries}</p>
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="charts-grid">
          {/* Monthly Trends Chart */}
          <div className="chart-card clickable" onClick={() => setShowGraphModal(true)}>
            <h3>Monthly Trends</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.monthlyTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="submissions" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Coverage Distribution Chart */}
          <div className="chart-card">
            <h3>Coverage Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart width={200} height={200}>
                <Pie
                  data={Object.entries(stats.coverageDistribution).map(([name, value]) => ({
                    name,
                    value
                  }))}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  fill="#f97316"
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {Object.entries(stats.coverageDistribution).map((entry, index) => (
                    <Cell key={`cell-${index}`} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Submissions */}
        <div className="recent-submissions">
          <div className="submissions-header">
            <h3>Recent Submissions</h3>
            <FontAwesomeIcon
              icon={faExpand}
              className="expand-icon"
              onClick={() => setShowAllSubmissions(true)}
            />
          </div>
          <div className="modal-search-container">
            <div className="modal-search-bar">
              <FontAwesomeIcon icon={faSearch} className="modal-search-icon" />
              <input
                type="text"
                placeholder="Search by name, email, phone, or cover option..."
                value={dashboardSearchTerm}
                onChange={(e) => setDashboardSearchTerm(e.target.value)}
                className="modal-search-input"
              />
              {dashboardSearchTerm && (
                <FontAwesomeIcon 
                  icon={faTimes} 
                  className="modal-clear-search" 
                  onClick={() => setDashboardSearchTerm('')}
                />
              )}
            </div>
            <p className="modal-results-count">
              Showing {filteredDashboardUsers.length} of {submissions.length} users
            </p>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th className="text-left">Date</th>
                  <th className="text-left">Name</th>
                  <th className="text-left">Email</th>
                  <th className="text-left">Phone</th>
                  <th className="text-left">Cover Option</th>
                  <th className="text-right">Annual Premium</th>
                  <th className="text-center">Beneficiaries</th>
                </tr>
              </thead>
              <tbody>
                {filteredDashboardUsers.slice(0, RECENT_SUBMISSIONS_COUNT).map((user) => (
                  <tr key={user.id}>
                    <td className="text-left">{new Date(user.submittedAt).toLocaleDateString()}</td>
                    <td className="text-left">{`${user.firstName} ${user.lastName}`}</td>
                    <td className="text-left">{user.email}</td>
                    <td className="text-left">{`${user.countryCode} ${user.phoneNumber}`}</td>
                    <td className="text-left">{user.coverOption}</td>
                    <td className="text-right">₵{Number(user.annualPremium).toLocaleString()}</td>
                    <td className="text-center">{user.beneficiaries?.length || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modals */}
        {showAllSubmissions && <SubmissionsModal />}
        {showAllUsers && <UsersModal sortField={sortField} sortDirection={sortDirection} handleSortChange={handleSortChange}/>}
        {showPremiumBreakdown && <PremiumBreakdownModal />}
        {showGraphModal && <GraphModal />}
      </div>
      <Footer />
    </>
  );
};

export default AdminDashboard;